import React, { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  getDocs,
  orderBy,
  where,
  limit,
} from 'firebase/firestore';
import { db } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();
const generateWashCodesFn = httpsCallable(functions, 'generateWashCodes');
const deleteWashCodesFn = httpsCallable(functions, 'deleteWashCodes');
const PAGE_SIZE = 50;

function AdminWashCodes() {
  const [washCodes, setWashCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sortMode, setSortMode] = useState('mostRecent');
  const [searchDate, setSearchDate] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [lastGenerated, setLastGenerated] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [copyFeedback, setCopyFeedback] = useState('');

  const closeConfirm = () => setConfirmDialog(null);

  const requestConfirm = (title, message, confirmLabel, onConfirm, confirmColor = '#ef4444') => {
    setConfirmDialog({ title, message, confirmLabel, onConfirm, confirmColor });
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog?.onConfirm) return;
    const action = confirmDialog.onConfirm;
    closeConfirm();
    await action();
  };

  const fetchWashCodes = useCallback(async () => {
    setLoading(true);
    try {
      let q;
      if (searchDate) {
        q = query(
          collection(db, 'washCodes'),
          where('createdDate', '==', searchDate),
          orderBy('createdAt', 'desc'),
          limit(500)
        );
      } else {
        q = query(
          collection(db, 'washCodes'),
          orderBy('createdAt', 'desc'),
          limit(500)
        );
      }
      const snapshot = await getDocs(q);
      const codes = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.() || null,
      }));
      setWashCodes(codes);
      setSelectedIds(new Set());
      setCurrentPage(0);
    } catch (error) {
      console.error('Error fetching wash codes:', error);
      alert('Failed to load wash codes. If filtering by date, ensure Firestore indexes are deployed.');
    } finally {
      setLoading(false);
    }
  }, [searchDate]);

  useEffect(() => {
    fetchWashCodes();
  }, [fetchWashCodes]);

  useEffect(() => {
    setCurrentPage(0);
  }, [sortMode, searchDate]);

  const runGenerate = async () => {
    setGenerating(true);
    try {
      const result = await generateWashCodesFn();
      const data = result.data;
      setLastGenerated(data.codes || []);
      alert(`Successfully generated ${data.count} wash codes.`);
      await fetchWashCodes();
    } catch (error) {
      console.error('Error generating wash codes:', error);
      alert(error.message || 'Failed to generate wash codes.');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerate = () => {
    requestConfirm(
      'Generate Wash Codes',
      'Generate 300 new wash codes? They will be stored in the database and cannot be recovered if deleted.',
      'Generate Codes',
      runGenerate,
      '#f97316'
    );
  };

  const runDeleteSelected = async () => {
    setDeleting(true);
    try {
      await deleteWashCodesFn({ codeIds: Array.from(selectedIds) });
      alert('Selected codes deleted.');
      await fetchWashCodes();
    } catch (error) {
      console.error('Error deleting codes:', error);
      alert(error.message || 'Failed to delete codes.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) {
      alert('Select at least one code to delete.');
      return;
    }
    requestConfirm(
      'Delete Selected Codes',
      `Delete ${selectedIds.size} selected code(s)? This action cannot be undone.`,
      'Delete Codes',
      runDeleteSelected
    );
  };

  const runDeleteByDate = async (dateToDelete) => {
    setDeleting(true);
    try {
      const result = await deleteWashCodesFn({ date: dateToDelete });
      alert(`Deleted ${result.data.deletedCount || 0} code(s) for ${dateToDelete}.`);
      await fetchWashCodes();
    } catch (error) {
      console.error('Error deleting codes by date:', error);
      alert(error.message || 'Failed to delete codes.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteByDate = () => {
    const dateToDelete = searchDate;
    if (!dateToDelete) {
      alert('Enter a date in the search filter first, then delete codes for that date.');
      return;
    }
    requestConfirm(
      'Delete Codes by Date',
      `Delete ALL wash codes created on ${dateToDelete}? This action cannot be undone.`,
      'Delete All for Date',
      () => runDeleteByDate(dateToDelete)
    );
  };

  const copyToClipboard = async (text, successMessage) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(successMessage);
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      alert('Failed to copy to clipboard.');
    }
  };

  const handleCopyCode = (code) => {
    copyToClipboard(code, 'Code copied');
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const formatDateTime = (date) => {
    if (!date) return '—';
    return date.toLocaleString();
  };

  const displayCodes = sortMode === 'mostRecent'
    ? [...washCodes].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    : washCodes;

  const totalPages = Math.max(1, Math.ceil(displayCodes.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages - 1);
  const pageStart = safePage * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, displayCodes.length);
  const paginatedCodes = displayCodes.slice(pageStart, pageEnd);
  const pageIds = paginatedCodes.map((c) => c.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const handleCopySelected = () => {
    if (selectedIds.size === 0) {
      alert('Select at least one code to copy.');
      return;
    }
    const selectedCodes = displayCodes
      .filter((item) => selectedIds.has(item.id))
      .map((item) => item.code);
    copyToClipboard(
      selectedCodes.join('\n'),
      `Copied ${selectedCodes.length} code(s)`
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ color: '#1f2937', fontSize: '1.8rem', margin: 0 }}>Wash Codes</h2>
        <button
          onClick={fetchWashCodes}
          disabled={loading}
          style={{
            background: '#10b981',
            color: '#fff',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem',
          }}
        >
          🔄 Refresh
        </button>
      </div>

      <div style={{
        background: '#fff',
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        marginBottom: '1.5rem',
      }}>
        <p style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '0.95rem' }}>
          Generate secure 64-character alphanumeric wash codes. Codes are stored in Firestore for verification and auditing.
        </p>
        <button
          onClick={handleGenerate}
          disabled={generating}
          style={{
            background: generating ? '#9ca3af' : '#f97316',
            color: '#fff',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            cursor: generating ? 'not-allowed' : 'pointer',
            fontWeight: 'bold',
            fontSize: '1rem',
          }}
        >
          {generating ? 'Generating...' : 'Generate 300 Wash Codes'}
        </button>
      </div>

      <div style={{
        background: '#fff',
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        marginBottom: '1.5rem',
      }}>
        <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1.1rem' }}>Filter & Search</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
              Sort
            </label>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e5e7eb' }}
            >
              <option value="mostRecent">Most Recent</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
              Search by Date
            </label>
            <input
              type="date"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e5e7eb' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={fetchWashCodes}
              style={{
                background: '#3b82f6',
                color: '#fff',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Apply Filter
            </button>
            <button
              onClick={() => { setSearchDate(''); }}
              style={{
                background: '#e5e7eb',
                color: '#374151',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Clear Date
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={handleCopySelected}
            disabled={selectedIds.size === 0}
            style={{
              background: selectedIds.size === 0 ? '#e5e7eb' : '#6366f1',
              color: selectedIds.size === 0 ? '#9ca3af' : '#fff',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            Copy Selected ({selectedIds.size})
          </button>
          <button
            onClick={handleDeleteSelected}
            disabled={deleting || selectedIds.size === 0}
            style={{
              background: selectedIds.size === 0 ? '#e5e7eb' : '#ef4444',
              color: selectedIds.size === 0 ? '#9ca3af' : '#fff',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            Delete Selected ({selectedIds.size})
          </button>
          <button
            onClick={handleDeleteByDate}
            disabled={deleting || !searchDate}
            style={{
              background: !searchDate ? '#e5e7eb' : '#dc2626',
              color: !searchDate ? '#9ca3af' : '#fff',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: !searchDate ? 'not-allowed' : 'pointer',
            }}
          >
            Delete All for Date
          </button>
          {copyFeedback && (
            <span style={{ color: '#059669', fontSize: '0.9rem', fontWeight: '500' }}>
              {copyFeedback}
            </span>
          )}
        </div>
      </div>

      {lastGenerated.length > 0 && (
        <div style={{
          background: '#ecfdf5',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #a7f3d0',
          marginBottom: '1.5rem',
        }}>
          <strong>Last generated batch: {lastGenerated.length} codes</strong>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: '#065f46' }}>
            Codes are listed below and saved in the database.
          </p>
        </div>
      )}

      <div style={{
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#f9fafb',
        }}>
          <span style={{ fontWeight: '600', color: '#1f2937' }}>
            {loading
              ? 'Loading...'
              : displayCodes.length === 0
                ? '0 code(s)'
                : `Showing ${pageStart + 1}–${pageEnd} of ${displayCodes.length} code(s)`}
            {searchDate ? ` for ${searchDate}` : ''}
          </span>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
            <input
              type="checkbox"
              checked={allPageSelected}
              onChange={toggleSelectAll}
              disabled={paginatedCodes.length === 0}
            />
            Select all on page
          </label>
        </div>
        <div>
          {paginatedCodes.length === 0 && !loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
              No wash codes yet. Click &quot;Generate 300 Wash Codes&quot; to create a batch.
            </div>
          ) : (
            paginatedCodes.map((item) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  padding: '0.75rem 1.5rem',
                  borderBottom: '1px solid #f3f4f6',
                  fontSize: '0.85rem',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(item.id)}
                  onChange={() => toggleSelect(item.id)}
                  style={{ marginTop: '0.25rem' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <code style={{
                    display: 'block',
                    wordBreak: 'break-all',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    color: '#1f2937',
                    background: '#f3f4f6',
                    padding: '0.5rem',
                    borderRadius: '4px',
                  }}>
                    {item.code}
                  </code>
                  <div style={{ marginTop: '0.35rem', color: '#6b7280', fontSize: '0.8rem' }}>
                    Created: {formatDateTime(item.createdAt)}
                    {item.createdDate ? ` · ${item.createdDate}` : ''}
                    {item.status ? ` · ${item.status}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopyCode(item.code)}
                  title="Copy code"
                  style={{
                    background: '#e0e7ff',
                    color: '#4338ca',
                    border: 'none',
                    padding: '0.35rem 0.6rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    flexShrink: 0,
                  }}
                >
                  Copy
                </button>
              </div>
            ))
          )}
        </div>
        {displayCodes.length > PAGE_SIZE && (
          <div style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#f9fafb',
          }}>
            <button
              type="button"
              onClick={goToPreviousPage}
              disabled={safePage === 0 || loading}
              style={{
                background: safePage === 0 ? '#e5e7eb' : '#3b82f6',
                color: safePage === 0 ? '#9ca3af' : '#fff',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: safePage === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '600',
              }}
            >
              ← Previous
            </button>
            <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
              Page {safePage + 1} of {totalPages}
            </span>
            <button
              type="button"
              onClick={goToNextPage}
              disabled={safePage >= totalPages - 1 || loading}
              style={{
                background: safePage >= totalPages - 1 ? '#e5e7eb' : '#3b82f6',
                color: safePage >= totalPages - 1 ? '#9ca3af' : '#fff',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                fontWeight: '600',
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {confirmDialog && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
          onClick={closeConfirm}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '1.5rem',
              maxWidth: '420px',
              width: '100%',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 0.75rem 0', color: '#1f2937', fontSize: '1.25rem' }}>
              {confirmDialog.title}
            </h3>
            <p style={{ margin: '0 0 1.5rem 0', color: '#6b7280', lineHeight: 1.5 }}>
              {confirmDialog.message}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={closeConfirm}
                style={{
                  background: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  padding: '0.6rem 1.25rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmAction}
                style={{
                  background: confirmDialog.confirmColor,
                  color: '#fff',
                  border: 'none',
                  padding: '0.6rem 1.25rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                }}
              >
                {confirmDialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminWashCodes;
